import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../components/Button/Button';
import type { IdArrayInput } from '../../hooks/useTablePages';
import useNotificationActions from '../../components/ActivityFeed/useNotificationActions';

type ActivityActionsProps = {
  selectedIds: IdArrayInput;
  selectedHasUnread: boolean;
  selectedHasFollowUp: boolean;
  selectedHasDismissed: boolean;
  onActionComplete?: () => void | Promise<void>;
};

const ActivityActions: React.FC<ActivityActionsProps> = ({
  selectedIds,
  selectedHasUnread,
  selectedHasFollowUp,
  selectedHasDismissed,
  onActionComplete,
}) => {
  const {
    markRead,
    markUnread,
    markAsFollowUp,
    unMarkAsFollowUp,
    dismiss,
    undismiss,
    loading,
  } = useNotificationActions();
  const [hasUnread, setHasUnread] = useState(selectedHasUnread);
  const [hasFollowUp, setHasFollowUp] = useState(selectedHasFollowUp);
  const [hasDismissed, setHasDismissed] = useState(selectedHasDismissed);
  const disabled = loading || selectedIds.length === 0;

  useEffect(() => {
    setHasUnread(selectedHasUnread);
    setHasFollowUp(selectedHasFollowUp);
    setHasDismissed(selectedHasDismissed);
  }, [selectedHasDismissed, selectedHasFollowUp, selectedHasUnread]);

  const toggleSelectedRead = useCallback(async () => {
    const action = hasUnread ? markRead : markUnread;
    await Promise.all(
      selectedIds.map((notificationId) => action(notificationId))
    );
    setHasUnread(!hasUnread);
    await onActionComplete?.();
  }, [hasUnread, markRead, markUnread, onActionComplete, selectedIds]);

  const toggleSelectedFollowUp = useCallback(async () => {
    const action = hasFollowUp ? unMarkAsFollowUp : markAsFollowUp;
    await Promise.all(
      selectedIds.map((notificationId) => action(notificationId))
    );
    setHasFollowUp(!hasFollowUp);
    await onActionComplete?.();
  }, [
    hasFollowUp,
    markAsFollowUp,
    onActionComplete,
    selectedIds,
    unMarkAsFollowUp,
  ]);

  const toggleSelectedDismissed = useCallback(async () => {
    const action = hasDismissed ? undismiss : dismiss;
    await Promise.all(
      selectedIds.map((notificationId) => action(notificationId))
    );
    setHasDismissed(!hasDismissed);
    await onActionComplete?.();
  }, [dismiss, hasDismissed, onActionComplete, selectedIds, undismiss]);

  return (
    <>
      <Button
        variant="outline"
        icon={hasUnread ? 'mail-open' : 'mail'}
        disabled={disabled}
        onClick={toggleSelectedRead}
        title={hasUnread ? 'Mark as read' : 'Mark as unread'}
      >
        {hasUnread ? 'Mark as read' : 'Mark as unread'}
      </Button>
      <Button
        variant="outline"
        icon={hasFollowUp ? 'bookmark' : 'bookmark-fill'}
        disabled={disabled}
        onClick={toggleSelectedFollowUp}
        title={hasFollowUp ? 'Unmark as follow-up' : 'Mark as follow-up'}
      >
        {hasFollowUp ? 'Unmark as follow-up' : 'Mark as follow-up'}
      </Button>
      <Button
        variant="outline"
        icon={hasDismissed ? 'eye' : 'x'}
        disabled={disabled}
        onClick={toggleSelectedDismissed}
        title={hasDismissed ? 'Restore' : 'Clear'}
      >
        {hasDismissed ? 'Restore' : 'Clear'}
      </Button>
    </>
  );
};

export default ActivityActions;
